// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useCallback, useEffect, useState } from "react";
import {
  addBaseUrlToImages,
  renameSubpartsKey,
  transformSubpartsKey,
} from "../utils/utilityFunctions";
import { convertToFormData } from "../config/convertToFormData";
import { useSelector } from "react-redux";
import { actionType } from "../utils/constant";
import toast from "react-hot-toast";

export default function useComponents(model, fetchTemplatesAPI) {
  const { profile } = useSelector((state) => state.profile);
  const { permissions } = profile;

  const [components, setComponents] = useState([]);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [currentComponent, setCurrentComponent] = useState(null);
  const [mode, setMode] = useState("view");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 100,
    total: 0,
    totalPages: 1,
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSelectedComponentId, setDeleteSelectedComponentId] =
    useState(null);

  const handleEdit = (component) => {
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }
    setCurrentComponent(component);
    setMode("edit");
    setOpenFormDialog(true);
  };

  const handleCreate = () => {
    if (!permissions.actions.includes(actionType.template_create)) {
      toast.error("You don't have permission to create components");
      return;
    }
    setCurrentComponent(null);
    setMode("create");
    setOpenFormDialog(true);
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleDelete = async () => {
    try {
      if (!permissions.actions.includes(actionType.template_delete)) {
        toast.error("You don't have permission to delete components");
        return;
      }
      await deleteComponentAPI(deleteSelectedComponentId.component_id);
      await getAllComponentsThroughAPI();
      setDeleteModal(false);
      await fetchTemplatesAPI();
    } catch (error) {
      console.error("Error deleting component:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenFormDialog(false);
    setCurrentComponent(null);
    getAllComponentsThroughAPI();
  };

  const handleSubmit = async (values) => {
    try {
      if (mode === "create") {
        if (!permissions.actions.includes(actionType.template_create)) {
          toast.error("You don't have permission to create components");
          return;
        }
        const newComponent = {
          ...values,
          component_template_id: model?.component_template_id,
          model_id: model?.model_id,
        };
        await handleSaveComponentThroughAPI(newComponent);
      } else if (mode === "edit") {
        if (!permissions.actions.includes(actionType.template_edit)) {
          toast.error("You don't have permission to edit components");
          return;
        }
        if (values.items.filter((item) => !item.item_id)?.length > 0) {
          await handleUpdateItemsThroughAPI({
            component_id: values.component_id,
            items: values.items.filter((item) => !item.item_id),
          });
        }
        const newSubparts = [];
        const getItemsWithId = values.items.filter((item) => item.item_id);
        getItemsWithId.forEach((item) => {
          const subpartWithoutId = item.sub_parts.filter(
            (subpart) => !subpart.subpart_id
          );
          subpartWithoutId.forEach((subpart) => {
            newSubparts.push({
              item_id: item.item_id,
              ...subpart,
            });
          });
        });
        if (newSubparts?.length > 0) {
          for (const subpart of newSubparts) {
            await handleUpdateSubpartThroughAPI(subpart);
          }
        }
      }
      await getAllComponentsThroughAPI();
      await fetchTemplatesAPI();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving component:", error);
    }
  };

  // const getTemplateDetailsAPI = useCallback(async (id) => {
  //   setLoading(true);
  //   try {
  //     try {
  //       const response = await api.get(`/api/rfi-component-templates/${id}`);
  //       return {
  //         success: true,
  //         data: response?.data,
  //       };
  //     } catch (err) {
  //       console.error("Error fetching components:", err);
  //       return {
  //         success: false,
  //         data: null,
  //       };
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const handleUpdateItemsThroughAPI = async (componentData) => {
    const updatedData = transformSubpartsKey(componentData);
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }

    return await api
      .post(`/api/rfi-components/items-with-subparts`, updatedData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const handleUpdateSubpartThroughAPI = async (componentData) => {
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }
    return await api
      .put(`/api/rfi-components/subpart`, componentData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error updating component:", err);
        return { success: false, error: err };
      });
  };

  const getAllComponentsThroughAPI = useCallback(() => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view components");
      return;
    }
    if (!model?.component_template_id) return;
    setLoading(true);
    return api
      .get(`/api/rfi-components`, {
        params: {
          component_template_id: model?.component_template_id,
          page: pagination.page,
          perPage: pagination.perPage,
        },
      })
      .then((response) => {
        const getDataInDesiredFormat = renameSubpartsKey(response.data);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const updatedData = addBaseUrlToImages(
          getDataInDesiredFormat.data,
          baseUrl
        );
        setComponents(updatedData);
        setPagination((prev) => ({
          ...prev,
          total: response.data.count,
          totalPages: response.data.totalPages,
          page: response.data.currentPage,
          perPage: response.data.perPage,
        }));
        return;
      })
      .catch((err) => {
        console.error("Error fetching components:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    model?.component_template_id,
    pagination.page,
    pagination.perPage,
    setComponents,
    permissions.actions,
  ]);

  const handleSaveComponentThroughAPI = async (componentData) => {
    const formData = convertToFormData(componentData);
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to edit components");
      return;
    }

    return await api
      .post("/api/rfi-component-templates/", formData)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((err) => {
        console.error("Error saving component:", err);
        return { success: false, error: err };
      });
  };

  const deleteComponentAPI = async (componentId) => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete components");
      return;
    }
    try {
      await api.delete(`/api/rfi-components/${componentId}`);
      if (components.length === 1 && pagination.page > 1) {
        setPagination((prev) => ({
          ...prev,
          page: prev.page - 1,
        }));
      } else {
        getAllComponentsThroughAPI();
      }
      return;
    } catch (err) {
      console.error("Error deleting component:", err);
      return;
    }
  };

  useEffect(() => {
    if (!model?.component_template_id) return;
    if (permissions.actions.includes(actionType.template_view)) {
      getAllComponentsThroughAPI();
    }
  }, [
    getAllComponentsThroughAPI,
    model?.component_template_id,
    pagination.page,
    permissions.actions,
  ]);

  return {
    components,
    loading,
    openFormDialog,
    currentComponent,
    mode,
    pagination,
    handleEdit,
    handleCreate,
    handleDelete,
    handleCloseDialog,
    handleSubmit,
    deleteModal,
    setDeleteModal,
    setDeleteSelectedComponentId,
    deleteSelectedComponentId,
    handlePageChange,
  };
}
